// Employee / person domain constants shared across HRMS and lead forms.

export const GENDER_OPTIONS = [
  { label: 'Male', value: 'M' },
  { label: 'Female', value: 'F' },
];

export const SALUTATION_OPTIONS = [
  { label: 'Mr', value: 'Mr' },
  { label: 'Mrs', value: 'Mrs' },
  { label: 'Miss', value: 'Miss' },
];

export const EMPLOYEE_ACTION = {
  TRACK_LIVE_LOCATION: 'track-live-location',
  ASSIGN_AREAS: 'assign-areas',
};

export const ASSIGN_BRANCH_ROLE_TYPE_PARENT_ID = 'USER_SELECTABLE';
export const ASSIGN_BRANCH_PATH = '/dashboard/assignbranch';
export const ASSIGN_BRANCH_QUERY = {
  PARTY_ID: 'party_id',
  ROLE_TYPE: 'role_type',
  EMPLOYEE_NAME: 'employee_name',
  ROLE_NAME: 'role_name',
};

export const ASSIGN_BRANCH_TREE_TITLE_FALLBACK = 'employee';

export function getAssignBranchTreeTitle(employeeName) {
  const name = String(employeeName || '').trim() || ASSIGN_BRANCH_TREE_TITLE_FALLBACK;
  return `Assign Branch to "${name}"`;
}

export const ASSIGN_BRANCH_ORG_LEVEL_ROLE_TYPE = 'ORGANIZATION_UNIT';

export function getAssignBranchDissociateContent(unitName) {
  const name = String(unitName || '').trim() || 'this unit';
  return `Are you sure you want to remove "${name}" from this assignment?`;
}

export function getAssignBranchAssignLabel(count) {
  const numeric = Number(count) || 0;
  if (numeric < 1) {
    return "Assign";
  }
  return `Assign ${numeric} unit${numeric === 1 ? '' : 's'}`;
}

export function formatAssignBranchPanelTitle(title, count) {
  return `${title} (${Number(count) || 0})`;
}

export const EMPLOYEE_LIST_PATH = '/dashboard/hrms/employee-list';

export const EMPLOYEE_DETAILS_COLLAPSED_LINE_COUNT = 3;

export const EMPLOYEE_LIVE_LOCATION_PATH_SEGMENT = 'live-location';
export const EMPLOYEE_ASSIGN_AREAS_PATH_SEGMENT = 'assign-areas';

export const EMPLOYEE_ASSIGN_AREA_TITLE_FALLBACK = 'employee';
export const EMPLOYEE_ASSIGN_AREAS_QUERY = {
  NAME: 'name',
  USER_LOGIN_ID: 'user_login_id',
  BASE_BRANCH_PARTY_ID: 'base_branch_party_id',
};

export function getEmployeeBaseBranchPartyId(record) {
  return record?.base_location?.branch_party_id || record?.base_location?.party_id || '';
}

export function getEmployeeLiveLocationPath(employeeId, { name } = {}) {
  const path = `${EMPLOYEE_LIST_PATH}/${encodeURIComponent(employeeId)}/${EMPLOYEE_LIVE_LOCATION_PATH_SEGMENT}`;

  if (!name) {
    return path;
  }

  return `${path}?name=${encodeURIComponent(name)}`;
}

export function getEmployeeAssignAreasPath(
  employeeId,
  { name, userLoginId, baseBranchPartyId } = {},
) {
  const path = `${EMPLOYEE_LIST_PATH}/${encodeURIComponent(employeeId)}/${EMPLOYEE_ASSIGN_AREAS_PATH_SEGMENT}`;
  const params = new URLSearchParams();

  if (name) {
    params.set(EMPLOYEE_ASSIGN_AREAS_QUERY.NAME, name);
  }
  if (userLoginId) {
    params.set(EMPLOYEE_ASSIGN_AREAS_QUERY.USER_LOGIN_ID, userLoginId);
  }
  if (baseBranchPartyId) {
    params.set(EMPLOYEE_ASSIGN_AREAS_QUERY.BASE_BRANCH_PARTY_ID, baseBranchPartyId);
  }

  const query = params.toString();
  return query ? `${path}?${query}` : path;
}

export function formatEmployeeAssignAreaTitle({ name, userLoginId, partyId } = {}) {
  const displayName = String(name || '').trim() || EMPLOYEE_ASSIGN_AREA_TITLE_FALLBACK;
  const id = String(userLoginId || partyId || '').trim();

  if (!id) {
    return `Assign Geo area to "${displayName}"`;
  }

  return `Assign Geo area to "${displayName} (${id})"`;
}

export const EMPLOYEE_AREA_SCHEDULE_MODE = {
  WEEK: 'week',
  MONTH: 'month',
};

export const EMPLOYEE_AREA_SCHEDULE_EMPTY_VALUE = '—';

export const EMPLOYEE_AREA_SCHEDULE_SUMMARY_JOIN = ' · ';
export const EMPLOYEE_AREA_SCHEDULE_DATE_SEPARATOR = '–';
export const EMPLOYEE_AREA_SCHEDULE_VALUE_DATE_FORMAT = 'YYYY-MM-DD';
export const EMPLOYEE_AREA_SCHEDULE_PICKER_Z_INDEX = 13000;
export const EMPLOYEE_AREA_SCHEDULE_MONTH_SUMMARY_LIST_LIMIT = 6;
export const EMPLOYEE_AREA_SCHEDULE_DAY_COLUMNS = 7;

export const EMPLOYEE_AREA_SCHEDULE_MODE_OPTIONS = [
  { value: EMPLOYEE_AREA_SCHEDULE_MODE.WEEK, label: 'Days of week' },
  { value: EMPLOYEE_AREA_SCHEDULE_MODE.MONTH, label: 'Days of month' },
];

// Routing-plan API numbers Sunday as 1 through Saturday as 7.
export const EMPLOYEE_AREA_SCHEDULE_WEEKDAYS = [
  { value: 1, label: 'Sun', name: 'Sunday' },
  { value: 2, label: 'Mon', name: 'Monday' },
  { value: 3, label: 'Tue', name: 'Tuesday' },
  { value: 4, label: 'Wed', name: 'Wednesday' },
  { value: 5, label: 'Thu', name: 'Thursday' },
  { value: 6, label: 'Fri', name: 'Friday' },
  { value: 7, label: 'Sat', name: 'Saturday' },
];

export const EMPLOYEE_AREA_SCHEDULE_MONTH_DAYS = Array.from({ length: 31 }, (_, index) => index + 1);

export function formatEmployeeAreaScheduleRemoveContent(dayLabel) {
  return `This will remove ${dayLabel} from the route plan.`;
}

export function formatEmployeeAreaScheduleSelectionCount(weekCount, monthCount) {
  const week = Number(weekCount) || 0;
  const month = Number(monthCount) || 0;
  const parts = [];

  if (!week && !month) {
    return "None selected";
  }

  if (week === 1) {
    parts.push("1 weekday");
  } else if (week > 1) {
    parts.push(`${week} weekdays`);
  }

  if (month === 1) {
    parts.push("1 month day");
  } else if (month > 1) {
    parts.push(`${month} month days`);
  }

  return parts.join(EMPLOYEE_AREA_SCHEDULE_SUMMARY_JOIN);
}

export function resolveEmployeeListNavKey(pathname) {
  const raw = pathname?.startsWith('/') ? pathname.slice(1) : pathname || '';
  const listKey = EMPLOYEE_LIST_PATH.replace(/^\//, '');

  if (raw === listKey || raw.startsWith(`${listKey}/`)) {
    return listKey;
  }

  return '';
}

